import React, { useState } from 'react';
import { createLogger } from '@/lib/logger';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import * as Linking from 'expo-linking';
import Constants from 'expo-constants';
import { supabase } from '@/lib/supabase';
import { debugStore } from '@/lib/debugStore';
import { Mail, CheckCircle } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';

const log = createLogger('FORGOT_PW');

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ForgotPasswordScreen() {
  const router = useRouter();
  const { theme } = useTheme();
  const insets = useSafeAreaInsets();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [computedRedirectTo, setComputedRedirectTo] = useState<string>('');
  const [showFallbackWarning, setShowFallbackWarning] = useState(false);

  // Compute and store redirectTo on mount
  React.useEffect(() => {
    const redirectTo = getCurrentRedirectUrl();
    setComputedRedirectTo(redirectTo);
    debugStore.setPasswordResetAttempt({
      email: 'computed-on-mount',
      redirectTo,
      success: true,
      timestamp: Date.now(),
    });
  }, []);

  const validateEmail = (email: string): boolean => {
    return EMAIL_REGEX.test(email.trim());
  };

  const getCurrentRedirectUrl = () => {
    if (Platform.OS === 'web') {
      return 'https://douevenlift.com/reset-password';
    } else if (Constants.appOwnership === 'expo') {
      return Linking.createURL('reset-password');
    } else {
      return 'duel://reset-password';
    }
  };

  const handleResetPassword = async () => {
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    let redirectTo: string;
    let branch: string;

    if (Platform.OS === 'web') {
      redirectTo = 'https://douevenlift.com/reset-password';
      branch = 'web';
    } else if (Constants.appOwnership === 'expo') {
      redirectTo = Linking.createURL('reset-password');
      branch = 'expo-go';
    } else {
      redirectTo = 'duel://reset-password';
      branch = 'dev-build/production';
    }

    setComputedRedirectTo(redirectTo);

    if (__DEV__) {
      log.info('[FORGOT_PASSWORD] Initiating password reset');
      log.info('[FORGOT_PASSWORD] Platform:', Platform.OS, 'Branch:', branch);
      log.info('[FORGOT_PASSWORD] Email:', trimmedEmail);
      log.info('[FORGOT_PASSWORD] Redirect URL:', redirectTo);
    }

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(trimmedEmail, {
        redirectTo,
      });

      if (resetError) {
        log.error('[FORGOT_PASSWORD] Supabase error:', {
          message: resetError.message,
          status: resetError.status,
          code: (resetError as any).code,
          name: resetError.name,
          fullError: resetError,
        });

        debugStore.setPasswordResetAttempt({
          email: trimmedEmail,
          redirectTo,
          success: false,
          error: {
            message: resetError.message,
            status: resetError.status,
            code: (resetError as any).code,
          },
          timestamp: Date.now(),
        });

        if (resetError.status === 500 || resetError.message.toLowerCase().includes('unexpected_failure')) {
          throw new Error("Couldn't send the email. Please try again in a minute.");
        } else if (resetError.message.toLowerCase().includes('rate limit') ||
            resetError.message.toLowerCase().includes('email rate limit exceeded')) {
          throw new Error('Too many attempts. Please wait a few minutes before trying again.');
        } else if (resetError.message.includes('not authorized')) {
          throw new Error('Password reset is not available. Please contact support.');
        } else if (resetError.message.includes('redirect')) {
          throw new Error("Something went wrong. Please try again.");
        }
        throw new Error("Something went wrong. Please try again.");
      }

      log.info('[FORGOT_PASSWORD] Password reset email sent successfully');
      debugStore.setPasswordResetAttempt({
        email: trimmedEmail,
        redirectTo,
        success: true,
        timestamp: Date.now(),
      });

      if (Platform.OS === 'web' && !redirectTo.includes('reset-password')) {
        log.warn('[FORGOT_PASSWORD] WARNING: Supabase may have used Site URL fallback');
        setShowFallbackWarning(true);
      }

      setSuccess(true);
    } catch (err: any) {
      log.error('[FORGOT_PASSWORD] Error:', {
        message: err.message,
        fullError: err,
      });
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <View style={styles.successContainer}>
          <View style={[styles.successIconContainer, { backgroundColor: theme.colors.surface, borderColor: theme.colors.success }]}>
            <CheckCircle size={64} color={theme.colors.success} />
          </View>
          <Text style={[styles.successTitle, { color: theme.colors.text }]}>Check Your Email</Text>
          <Text style={[styles.successMessage, { color: theme.colors.success }]}>Check your email for the reset link.</Text>
          <Text style={[styles.successSubtext, { color: theme.colors.textSecondary }]}>
            We sent a password reset link to {email.trim()}. Click the link in the email to reset
            your password. If you don&apos;t see it, check your spam folder.
          </Text>

          {showFallbackWarning && (
            <View style={[styles.warningBox, { backgroundColor: theme.colors.surface, borderColor: theme.colors.accent }]}>
              <Text style={[styles.warningTitle, { color: theme.colors.accent }]}>Warning: Site URL Fallback</Text>
              <Text style={[styles.warningText, { color: theme.colors.text }]}>
                Supabase may have used Site URL fallback. If the email link goes to the homepage
                instead of reset-password, check that the Redirect URLs allow-list includes:
              </Text>
              <Text style={[styles.warningUrl, { color: theme.colors.accent, backgroundColor: theme.colors.background }]}>{computedRedirectTo}</Text>
              <Text style={[styles.warningText, { color: theme.colors.text }]}>
                Go to: Supabase Dashboard ?+&apos; Authentication ?+&apos; URL Configuration ?+&apos; Redirect URLs
              </Text>
            </View>
          )}

          <TouchableOpacity
            style={[styles.backToSignInButton, { backgroundColor: theme.colors.accent }]}
            onPress={() => router.push('/(auth)/signin')}>
            <Text style={[styles.backToSignInText, { color: theme.colors.accentForeground }]}>Back to Sign In</Text>
          </TouchableOpacity>

          <TouchableOpacity onPress={() => setSuccess(false)}>
            <Text style={[styles.resendText, { color: theme.colors.textTertiary }]}>Didn&apos;t receive the email? Try again</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <View style={styles.header}>
        <View style={{ width: 40 }} />
      </View>

      <View style={styles.content}>
        <View style={[styles.iconContainer, { backgroundColor: theme.colors.surface, borderColor: theme.colors.accent }]}>
          <Mail size={48} color={theme.colors.accent} />
        </View>

        <Text style={[styles.title, { color: theme.colors.text }]}>Forgot Password?</Text>
        <Text style={[styles.subtitle, { color: theme.colors.textSecondary }]}>
          Enter your email address and we&apos;ll send you a link to reset your password
        </Text>

        {error ? <Text style={[styles.errorText, { color: theme.colors.accent, backgroundColor: theme.colors.surface, borderColor: theme.colors.accent }]}>{error}</Text> : null}

        <View style={styles.form}>
          <View style={styles.inputContainer}>
            <Text style={[styles.label, { color: theme.colors.text }]}>Email Address</Text>
            <TextInput
              style={[styles.input, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border, color: theme.colors.text }]}
              value={email}
              onChangeText={text => {
                setEmail(text);
                setError('');
              }}
              placeholder="your@email.com"
              placeholderTextColor={theme.colors.textTertiary}
              autoCapitalize="none"
              keyboardType="email-address"
              editable={!loading}
              autoFocus
            />
          </View>

          <Pressable
            style={[styles.button, { backgroundColor: theme.colors.accent }, loading && styles.buttonDisabled]}
            onPress={handleResetPassword}
            disabled={loading}
            android_ripple={{ color: 'rgba(0,0,0,0.15)', borderless: false }}
            hitSlop={{ top: 8, bottom: 8 }}>
            {loading ? (
              <ActivityIndicator color={theme.colors.accentForeground} />
            ) : (
              <Text style={[styles.buttonText, { color: theme.colors.accentForeground }]}>Send Reset Link</Text>
            )}
          </Pressable>

          <TouchableOpacity
            onPress={() => router.push('/(auth)/signin')}
            disabled={loading}
            style={styles.cancelButton}>
            <Text style={[styles.cancelText, { color: theme.colors.textSecondary }]}>Cancel</Text>
          </TouchableOpacity>
        </View>
      </View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 20,
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    paddingHorizontal: 32,
    paddingTop: 20,
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 4,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
    alignSelf: 'center',
  },
  title: {
    fontSize: 32,
    fontWeight: '900',
    marginBottom: 12,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 40,
    textAlign: 'center',
    lineHeight: 24,
  },
  form: {
    gap: 24,
  },
  inputContainer: {
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
  },
  input: {
    borderWidth: 1,
    borderRadius: 2,
    paddingHorizontal: 16,
    paddingVertical: 16,
    fontSize: 16,
  },
  button: {
    paddingVertical: 18,
    borderRadius: 2,
    alignItems: 'center',
    marginTop: 8,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  buttonText: {
    fontSize: 18,
    fontWeight: '700',
  },
  cancelButton: {
    paddingVertical: 12,
    alignItems: 'center',
  },
  cancelText: {
    fontSize: 16,
    fontWeight: '600',
  },
  errorText: {
    fontSize: 14,
    textAlign: 'center',
    padding: 12,
    borderRadius: 2,
    borderWidth: 1,
  },
  successContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  successIconContainer: {
    width: 120,
    height: 120,
    borderRadius: 4,
    borderWidth: 3,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 32,
  },
  successTitle: {
    fontSize: 28,
    fontWeight: '900',
    marginBottom: 16,
    textAlign: 'center',
  },
  successMessage: {
    fontSize: 16,
    marginBottom: 12,
    textAlign: 'center',
    fontWeight: '600',
  },
  successSubtext: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 40,
  },
  backToSignInButton: {
    paddingVertical: 16,
    paddingHorizontal: 48,
    borderRadius: 2,
    marginBottom: 20,
  },
  backToSignInText: {
    fontSize: 16,
    fontWeight: '700',
  },
  resendText: {
    fontSize: 14,
    textDecorationLine: 'underline',
  },
  warningBox: {
    borderWidth: 2,
    borderRadius: 2,
    padding: 16,
    marginTop: 24,
    marginBottom: 24,
  },
  warningTitle: {
    fontSize: 15,
    fontWeight: '700',
    marginBottom: 12,
  },
  warningText: {
    fontSize: 13,
    lineHeight: 20,
    marginBottom: 8,
  },
  warningUrl: {
    fontSize: 12,
    fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
    padding: 8,
    borderRadius: 4,
    marginVertical: 8,
  },
});
